import { Component, Story, Knobs } from './DocTypes';

const PlaygroundStoryKnobs: Knobs = {
  customColour: { default: '#BADA55', editor: 'textbox' },
};

const PlaygroundStoryKnobs_v3: Knobs = {
  colourScheme: {
    editor: 'dropdown',
    editorOptions: ['light', 'dark', 'default', ''],
    default: '',
  },
  device: {
    editor: 'dropdown',
    editorOptions: ['mobile', 'desktop', ''],
    default: '',
  },
};

const PlaygroundStory: Story = {
  title: 'Playground',
  description: 'A colour picker',
  coreComponent: {
    html: `<tag-colour{{...knobs}}>
  <tag-colour-option text="White" accent="white" accent-selected="title"></tag-colour-option>
  <tag-colour-option text="Access red" accent="access" accent-selected="white"></tag-colour-option>
  <tag-colour-option text="Midnight blue" accent="midnightblue" accent-selected="white"></tag-colour-option>
</tag-colour>`,
    knobs: PlaygroundStoryKnobs,
  },
  coreComponent_v3: {
    html: `
      <tag3-colour {{...knobs}}>
        <tag3-colour-option text="White" accent="white" accent-selected="title"></tag3-colour-option>
        <tag3-colour-option text="Access red" accent="access" accent-selected="white"></tag3-colour-option>
        <tag3-colour-option text="Midnight blue" accent="midnightblue" accent-selected="white"></tag3-colour-option>
      </tag3-colour>
    `,
    knobs: { ...PlaygroundStoryKnobs, ...PlaygroundStoryKnobs_v3 },
  },
  reactWrapper: {
    jsx: `<TagColour{{...knobs}}
      colourOptions={[
        { text: 'White', accent: 'white', accentSelected: 'title'},
        { text: 'Access red', accent: 'access', accentSelected: 'white' },
        { text: 'Midnight blue', accent: 'midnightblue', accentSelected: 'white' },
      ]}
    />`,
    knobs: PlaygroundStoryKnobs,
  },
  reactWrapper_v3: {
    jsx: `<Tag3Colour{{...knobs}}
      colourOptions={[
        { text: 'White', accent: 'white', accentSelected: 'title'},
        { text: 'Access red', accent: 'access', accentSelected: 'white' },
        { text: 'Midnight blue', accent: 'midnightblue', accentSelected: 'white' },
      ]}
    />`,
    knobs: { ...PlaygroundStoryKnobs, ...PlaygroundStoryKnobs_v3 },
  },
};

const ModalStory: Story = {
  title: 'Modal',
  description: 'A colour picker inside a modal dialog',
  coreComponent: {
    html: `<tag-modal visible heading='Colour picker in modal'>
      <div style="position: relative">
        <tag-colour></tag-colour>
      </div>
    </tag-modal>`,
  },
  coreComponent_v3: {
    html: `<tag-modal visible heading='Colour picker in modal'>
      <div style="position: relative">
        <tag3-colour></tag3-colour>
      </div>
    </tag-modal>`,
  },

  reactWrapper: {
    jsx: `<TagModal visible heading='Colour picker in modal'>
      <div style={{position: "relative"}}>
        <TagColour></TagColour>
      </div>
    </TagModal>`,
  },
  reactWrapper_v3: {
    jsx: `<TagModal visible heading='Colour picker in modal'>
      <div style={{position: "relative"}}>
        <Tag3Colour></Tag3Colour>
      </div>
    </TagModal>`,
  },
};

const SuppressCustomOptionStory: Story = {
  title: 'Suppress Custom Option',
  description: 'A colour picker without custom colour option',
  coreComponent_v3: {
    html: ` <tag3-colour suppress-custom>
      <tag3-colour-option text="Access" accent="access" accent-selected="white" selected></tag3-colour-option>
      <tag3-colour-option text="Coral" accent="coral" accent-selected="white"></tag3-colour-option>
      <tag3-colour-option text="Sunflower" accent="sunflower" accent-selected="white"></tag3-colour-option>
      <tag3-colour-option text="Viridiangreen" accent="viridiangreen" accent-selected="white"></tag3-colour-option>
      
    </tag3-colour>`,
  },

  reactWrapper_v3: {
    jsx: `<Tag3Colour suppressCustom
      colourOptions={[
        { text: 'Access', accent: 'access', accentSelected: 'white', selected: true },
        { text: 'Coral', accent: 'coral', accentSelected: 'white' },
        { text: 'Sunflower', accent: 'sunflower', accentSelected: 'white' },
        { text: 'Viridiangreen', accent: 'viridiangreen', accentSelected: 'white' },
      ]}
    />`,
  },
};

export const TagColourOption: Component = {
  type: 'component',
  name: 'colour-option',
  description: `Defines an option in tag-colour. This is specified as a nested element within tag-colour.`,
  mobileFriendly: false,
  popular: false,
  preRelease: '',
  props: [
    {
      name: 'accent',
      type: 'Accent',
      description: 'The accent of the colour option',
    },
    {
      name: 'accentSelected',
      type: 'Accent',
      description: 'The accent of the selected colour option',
    },
    {
      name: 'text',
      type: 'string',
      description: 'The text of the colour option',
    },
    {
      name: 'colourScheme',
      description:
        'V3 Only: Dark mode (theme) control. Default for normal browser driven control. Dark | Light to force respective styling regardless of browser setting.',
      type: 'dark | light | default',
      default: 'light',
    },
    {
      name: 'device',
      description: 'V3 Only: Set preferred styling theme for device type.',
      type: 'desktop | mobile',
      default: 'desktop',
    },
  ],
  reactPropOverrides: [],
  events: [
    {
      name: 'colourOptionClick',
      description: 'Raised when the colour option is changed',
      parameters: [{ name: 'accent', description: 'The accent', type: 'string' }],
    },
  ],
  methods: [],
  stories: [],
};

export const TagColourDocs: Component = {
  type: 'component',
  name: 'colour',
  description: 'Renders a colour picker',
  mobileFriendly: false,
  popular: false,
  preRelease: '',
  props: [
    {
      name: 'customColour',
      description: 'The custom colour value',
      type: 'string',
    },
    {
      name: 'suppressCustom',
      description: 'Whether to suppress custom colour option',
      type: 'boolean',
    },
    {
      name: 'colourScheme',
      description:
        'V3 Only: Dark mode (theme) control. Default for normal browser driven control. Dark | Light to force respective styling regardless of browser setting.',
      type: 'dark | light | default',
      default: 'light',
    },
    {
      name: 'device',
      description: 'V3 Only: Set preferred styling theme for device type.',
      type: 'desktop | mobile',
      default: 'desktop',
    },
  ],
  reactPropOverrides: [],
  events: [
    {
      name: 'colourChange',
      description: 'Raised when the colour is changed',
      parameters: [{ name: 'value', description: 'The colour value', type: 'string' }],
    },
    {
      name: 'colourCustomOptionClick',
      description: 'Raised when the custom colour option is clicked',
      parameters: [{ name: 'customColour', description: 'The colour value', type: 'string' }],
    },
  ],
  methods: [],
  stories: [PlaygroundStory, ModalStory, SuppressCustomOptionStory],
  childComponents: [TagColourOption],
};
